import React, { useEffect, useRef, useState } from 'react';
import { button, input } from './editors';
import type { Book } from './model';

export type BookIntent = 'edit' | 'visibility';

/** Reversible unpublication, using the edition's existing audited visibility action. */
export function PublicationVisibility({
  book,
  disabled,
  busy,
  initiallyOpen = false,
  confirm,
}: {
  book: Book;
  disabled: boolean;
  busy: boolean;
  initiallyOpen?: boolean;
  confirm(action: 'hide' | 'restore', reason: string): void;
}) {
  const hidden = book.catalogueVisibility === 'hidden';
  const [open, setOpen] = useState(initiallyOpen);
  const [reason, setReason] = useState('');
  const reasonInput = useRef<HTMLTextAreaElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (open) reasonInput.current?.focus();
  }, [open]);
  const cancel = () => {
    setOpen(false);
    trigger.current?.focus();
  };
  return (
    <section className="mt-7 border-t border-line/65 pt-6" aria-label="Publication visibility">
      <h3 className="font-serif text-2xl">
        {hidden ? 'Removed from the public catalogue' : 'Public catalogue visibility'}
      </h3>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-ink-soft">
        {hidden
          ? 'This publication is unavailable in public discovery and cannot be added by new readers. Existing readers keep their library entries, progress, notes and quotes.'
          : 'Remove a harmful, inappropriate or incorrect publication from public discovery and prevent new library additions. Existing readers keep their library entries, progress, notes and quotes.'}
      </p>
      <button
        ref={trigger}
        type="button"
        className={`${hidden ? button : 'admin-button-danger'} mt-4`}
        disabled={disabled}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {hidden ? 'Restore publication' : 'Remove publication'}
      </button>
      {open && (
        <fieldset className="mt-4 rounded-xl border border-line p-4 sm:p-5" disabled={disabled}>
          <legend className="px-2 font-semibold">
            {hidden ? 'Confirm restoration' : 'Confirm removal'}
          </legend>
          <p className="mb-4 text-sm leading-6 text-ink-soft">
            {hidden
              ? `Restore “${book.title}” to the public catalogue? Readers will be able to discover and add it again.`
              : `Remove “${book.title}” from the public catalogue? Its moderation record is retained and you can restore it later.`}
          </p>
          <label className="block text-sm font-semibold">
            {hidden ? 'Restoration reason' : 'Removal reason'}
            <textarea
              ref={reasonInput}
              className={input}
              rows={3}
              required
              maxLength={1000}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              className={hidden ? 'admin-button-primary' : 'admin-button-danger'}
              disabled={!reason.trim()}
              onClick={() => confirm(hidden ? 'restore' : 'hide', reason.trim())}
            >
              {busy ? 'Saving…' : hidden ? 'Confirm restoration' : 'Confirm removal'}
            </button>
            <button type="button" className={button} onClick={cancel}>
              Cancel
            </button>
          </div>
        </fieldset>
      )}
    </section>
  );
}
